import React, { useState, useEffect, useRef } from "react";
import { API_BASE_URL } from "../config";
import { speakText } from "../utils/audioHelpers";
import { navigateToDashboard, getEffectiveLangId } from "../utils/navigationHelpers";
import { getLocalSong, saveLocalSong } from "../utils/songStorage";
import { authFetch } from "../utils/auth";
import LoadingScreen from "../components/common/LoadingScreen";
import BackToDashboardButton from "../components/common/BackToDashboardButton";
import "../assets/css/music.css";

const LANG_TAG = { 0: "de-DE", 1: "es-MX", 2: "fr-FR", 3: "ru-RU", 4: "pt-BR" };
function getLangTag(langId) {
    return LANG_TAG[parseInt(langId, 10)] ?? "de-DE";
}

let ytApiPromise = null;
function loadYouTubeIframeApi() {
    if (window.YT && window.YT.Player) {
        return Promise.resolve(window.YT);
    }
    if (ytApiPromise) return ytApiPromise;

    ytApiPromise = new Promise((resolve) => {
        const existingScript = document.getElementById("yt-iframe-api-script");
        if (!existingScript) {
            const tag = document.createElement("script");
            tag.id = "yt-iframe-api-script";
            tag.src = "https://www.youtube.com/iframe_api";
            document.head.appendChild(tag);
        }
        const prevReady = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
            if (prevReady) prevReady();
            resolve(window.YT);
        };
        const checkInterval = setInterval(() => {
            if (window.YT && window.YT.Player) {
                clearInterval(checkInterval);
                resolve(window.YT);
            }
        }, 100);
    });

    return ytApiPromise;
}

function tokenizeLyricLine(text) {
    if (!text) return [];
    const tokens = [];
    const regex = /([\p{L}\p{N}'-]+|[^\p{L}\p{N}'-]+)/gu;
    let match;
    while ((match = regex.exec(text)) !== null) {
        const raw = match[0];
        const clean = raw.replace(/^['"-]+|['"-]+$/g, "");
        const isWord = clean.length > 0 && /\p{L}/u.test(clean);
        tokens.push({
            raw,
            clean,
            isWord
        });
    }
    return tokens;
}

function VolumeIcon({ size = 15 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
        </svg>
    );
}

function PlayIcon({ size = 20 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
            <polygon points="5 3 19 12 5 21 5 3" />
        </svg>
    );
}

function PauseIcon({ size = 20 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="4" width="4" height="16" />
            <rect x="14" y="4" width="4" height="16" />
        </svg>
    );
}

function Rewind5Icon({ size = 18 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 19l-9-7 9-7v14z" />
            <path d="M22 19l-9-7 9-7v14z" />
        </svg>
    );
}

function Forward5Icon({ size = 18 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M13 19l9-7-9-7v14z" />
            <path d="M2 19l9-7-9-7v14z" />
        </svg>
    );
}

function TranslateIcon({ size = 16 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 8l6 6" />
            <path d="M4 14l6-6 2-3" />
            <path d="M2 5h12" />
            <path d="M7 2h1" />
            <path d="M22 22l-5-10-5 10" />
            <path d="M14 18h6" />
        </svg>
    );
}

function CheckmarkIcon({ size = 16 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
        </svg>
    );
}

function SongPage() {
    const urlParams = new URLSearchParams(window.location.search);
    const songId = urlParams.get("song_id");
    const langId = getEffectiveLangId(urlParams.get("lang_id")) || "0";

    const [song, setSong] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [localAudioUrl, setLocalAudioUrl] = useState(null);

    // Audio & playback state
    const audioRef = useRef(null);
    const ytPlayerRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [playbackRate, setPlaybackRate] = useState(1.0);
    const [activeLineIndex, setActiveLineIndex] = useState(-1);
    const [syncOffset, setSyncOffset] = useState(0);

    const ytVideoId = song?.source_video_id || (String(songId).startsWith("custom_") ? String(songId).replace(/^custom_[^_]+_/, "") : null);

    // UX Options
    const [showTranslations, setShowTranslations] = useState(false);
    const [autoScroll, setAutoScroll] = useState(true);
    const [savedWords, setSavedWords] = useState([]);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState(null);

    const lineElementsRef = useRef([]);
    const viewportRef = useRef(null);
    const userClickedLineRef = useRef(false);

    // Fetch song metadata and synced lyrics (from local IndexedDB if custom, or server if course)
    useEffect(() => {
        if (!songId) {
            setError(true);
            setLoading(false);
            return;
        }

        let isMounted = true;
        setLoading(true);

        const isCustom = String(songId).startsWith("custom_");

        if (isCustom) {
            // First check IndexedDB for local lyrics
            getLocalSong(songId).then(async (localRecord) => {
                if (!isMounted) return;

                const videoId = String(songId).replace(/^custom_[^_]+_/, "").replace(/[^a-zA-Z0-9_-]/g, "");

                // If not found directly, check legacy keys (custom_0_videoId or custom_user_videoId)
                if (!localRecord) {
                    const legacyIds = [`custom_0_${videoId}`, `custom_user_${videoId}`];
                    for (const legId of legacyIds) {
                        if (legId !== songId) {
                            const legRec = await getLocalSong(legId);
                            if (legRec && legRec.lyricsData) {
                                await saveLocalSong(songId, null, {
                                    ...legRec.lyricsData,
                                    id: songId,
                                    source_video_id: videoId
                                });
                                localRecord = legRec;
                                break;
                            }
                        }
                    }
                }

                if (localRecord && localRecord.lyricsData) {
                    setSong(localRecord.lyricsData);
                    if (localRecord.lyricsData.duration) {
                        setDuration(localRecord.lyricsData.duration);
                    }
                    setLoading(false);

                    // Check if cached custom song has missing/untranslated English lines or empty glossary
                    const cachedLines = localRecord.lyricsData.lines || [];
                    const translatedCount = cachedLines.filter((l) => {
                        const f = (l.foreign || l.text || "").trim().toLowerCase();
                        const e = (l.english || l.translation || "").trim().toLowerCase();
                        return e && e !== f;
                    }).length;
                    const hasMusicalNotes = cachedLines.some((l) =>
                        /[♪♫♩♬]/.test(l.foreign || l.text || "") || /[♪♫♩♬]/.test(l.english || l.translation || "")
                    );
                    const glossaryEmpty =
                        !localRecord.lyricsData.word_glossary ||
                        Object.keys(localRecord.lyricsData.word_glossary).length === 0;

                    if (
                        cachedLines.length === 0 ||
                        translatedCount < Math.max(1, Math.floor(cachedLines.length * 0.5)) ||
                        hasMusicalNotes ||
                        glossaryEmpty
                    ) {
                        authFetch(`${API_BASE_URL}/api/songs/process-preview`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                                video_id: videoId,
                                title: localRecord.lyricsData.title || "",
                                artist: localRecord.lyricsData.artist || "",
                                lang_id: langId
                            })
                        })
                            .then((res) => (res.ok ? res.json() : null))
                            .then(async (previewData) => {
                                if (!previewData || !previewData.lines || previewData.lines.length === 0) return;
                                const updatedLyricsData = {
                                    ...localRecord.lyricsData,
                                    id: songId,
                                    lines_count: previewData.lines.length,
                                    lines: previewData.lines,
                                    word_glossary: previewData.word_glossary || localRecord.lyricsData.word_glossary || {},
                                    source_video_id: videoId
                                };
                                await saveLocalSong(songId, null, updatedLyricsData);
                                if (isMounted) {
                                    setSong(updatedLyricsData);
                                }
                            })
                            .catch((err) => {
                                console.warn("Background lyrics refresh skipped:", err);
                            });
                    }
                } else {
                    // Fetch synced lyrics metadata from server (fast JSON metadata, no audio download)
                    try {
                        const previewRes = await authFetch(`${API_BASE_URL}/api/songs/process-preview`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                                video_id: videoId,
                                lang_id: langId
                            })
                        });
                        const previewData = await previewRes.json();

                        const lyricsData = {
                            id: songId,
                            title: previewData.title || "Custom Track",
                            artist: previewData.artist || "",
                            duration: previewData.duration || 0,
                            lines_count: (previewData.lines && previewData.lines.length) || 0,
                            lines: previewData.lines || [],
                            word_glossary: previewData.word_glossary || {},
                            source_video_id: videoId
                        };

                        await saveLocalSong(songId, null, lyricsData);
                        if (!isMounted) return;
                        setSong(lyricsData);
                        if (lyricsData.duration) {
                            setDuration(lyricsData.duration);
                        }
                    } catch (err) {
                        console.error("Failed to load custom song:", err);
                        if (isMounted) setError(true);
                    } finally {
                        if (isMounted) setLoading(false);
                    }
                }
            }).catch((err) => {
                console.error("IndexedDB error:", err);
                if (isMounted) {
                    setError(true);
                    setLoading(false);
                }
            });
        } else {
            // Preloaded course song from server
            fetch(`${API_BASE_URL}/api/songs/${songId}/lyrics`)
                .then((res) => {
                    if (!res.ok) throw new Error("Failed to load song");
                    return res.json();
                })
                .then((data) => {
                    if (!isMounted) return;
                    setSong(data);
                    setLocalAudioUrl(`${API_BASE_URL}/api/songs/${songId}/audio`);
                    if (data.duration) {
                        setDuration(data.duration);
                    }
                })
                .catch((err) => {
                    console.error("Error fetching song lyrics:", err);
                    if (isMounted) setError(true);
                })
                .finally(() => {
                    if (isMounted) setLoading(false);
                });
        }

        return () => {
            isMounted = false;
        };
    }, [songId, langId]);

    // Fetch existing saved words for this user and song
    useEffect(() => {
        if (!songId) return;
        authFetch(`${API_BASE_URL}/api/song-words?song_id=${songId}&lang_id=${langId}`)
            .then((res) => res.json())
            .then((data) => {
                if (data && data.words) {
                    setSavedWords(data.words);
                }
            })
            .catch((err) => {
                console.error("Error fetching saved song words:", err);
            });
    }, [songId, langId]);

    // Fast lookup set of saved words (lowercase)
    const savedWordsSet = new Set(
        savedWords.map((w) => (w.word || "").toLowerCase())
    );

    // Auto-scroll to active lyric line (only during automated playback progression)
    useEffect(() => {
        if (!autoScroll || activeLineIndex === -1) return;
        if (userClickedLineRef.current) {
            // User explicitly clicked this line; do not shift the viewport
            userClickedLineRef.current = false;
            return;
        }
        const container = viewportRef.current;
        const el = lineElementsRef.current[activeLineIndex];
        if (container && el) {
            const elTop = el.offsetTop;
            const elHeight = el.offsetHeight;
            const containerHeight = container.clientHeight;
            container.scrollTo({
                top: elTop - containerHeight / 2 + elHeight / 2,
                behavior: "smooth"
            });
        }
    }, [activeLineIndex, autoScroll]);

    // Initialize YouTube IFrame Player when ytVideoId is available
    useEffect(() => {
        if (!ytVideoId) return;

        let player = null;
        let isCancelled = false;

        loadYouTubeIframeApi().then((YT) => {
            if (isCancelled) return;

            const container = document.getElementById("yt-player-embed");
            if (!container) return;

            player = new YT.Player("yt-player-embed", {
                videoId: ytVideoId,
                playerVars: {
                    autoplay: 0,
                    controls: 0,
                    disablekb: 1,
                    fs: 0,
                    modestbranding: 1,
                    rel: 0,
                    playsinline: 1,
                    origin: window.location.origin
                },
                events: {
                    onReady: (event) => {
                        if (isCancelled) return;
                        ytPlayerRef.current = event.target;
                        const dur = event.target.getDuration();
                        if (dur && dur > 0) {
                            setDuration(dur);
                        }
                    },
                    onStateChange: (event) => {
                        if (isCancelled) return;
                        // 1 = PLAYING, 2 = PAUSED, 0 = ENDED
                        if (event.data === 1) {
                            setIsPlaying(true);
                            const dur = event.target.getDuration();
                            if (dur && dur > 0) setDuration(dur);
                        } else if (event.data === 2 || event.data === 0) {
                            setIsPlaying(false);
                        }
                    }
                }
            });
        });

        return () => {
            isCancelled = true;
            if (player && typeof player.destroy === "function") {
                try {
                    player.destroy();
                } catch (_) {}
            }
            ytPlayerRef.current = null;
        };
    }, [ytVideoId]);

    // High-frequency synchronization timer for YouTube and Audio (runs every 50ms when playing)
    useEffect(() => {
        if (!isPlaying || !song || !song.lines || !song.lines.length) return;

        const interval = setInterval(() => {
            let currentSec = 0;
            if (ytPlayerRef.current && typeof ytPlayerRef.current.getCurrentTime === "function") {
                currentSec = ytPlayerRef.current.getCurrentTime() || 0;
            } else if (audioRef.current) {
                currentSec = audioRef.current.currentTime || 0;
            } else {
                return;
            }

            const currentMs = (currentSec + syncOffset) * 1000;
            setCurrentTime(currentSec);

            // Find current matching lyric line: chronological match to latest started line
            let index = -1;
            for (let i = 0; i < song.lines.length; i++) {
                if (currentMs >= song.lines[i].start_ms) {
                    index = i;
                } else {
                    break;
                }
            }

            if (index !== activeLineIndex) {
                setActiveLineIndex(index);
            }
        }, 50);

        return () => clearInterval(interval);
    }, [isPlaying, song, syncOffset, activeLineIndex]);

    // Time update handler from <audio> (fallback for course songs)
    const handleTimeUpdate = () => {
        if (ytPlayerRef.current) return;
        if (!audioRef.current || !song || !song.lines) return;
        const currentSec = audioRef.current.currentTime;
        const currentMs = (currentSec + syncOffset) * 1000;
        setCurrentTime(currentSec);

        let index = -1;
        for (let i = 0; i < song.lines.length; i++) {
            if (currentMs >= song.lines[i].start_ms) {
                index = i;
            } else {
                break;
            }
        }

        if (index !== activeLineIndex) {
            setActiveLineIndex(index);
        }
    };

    const handleLoadedMetadata = () => {
        if (audioRef.current) {
            setDuration(audioRef.current.duration || song?.duration || 0);
        }
    };

    const togglePlay = () => {
        if (ytPlayerRef.current) {
            if (isPlaying) {
                ytPlayerRef.current.pauseVideo();
                setIsPlaying(false);
            } else {
                ytPlayerRef.current.playVideo();
                setIsPlaying(true);
            }
            return;
        }

        if (!audioRef.current) return;
        if (isPlaying) {
            audioRef.current.pause();
            setIsPlaying(false);
        } else {
            audioRef.current.play().then(() => setIsPlaying(true)).catch(console.error);
        }
    };

    const handleSeek = (newTime) => {
        if (ytPlayerRef.current) {
            ytPlayerRef.current.seekTo(newTime, true);
            setCurrentTime(newTime);
            return;
        }
        if (!audioRef.current) return;
        audioRef.current.currentTime = newTime;
        setCurrentTime(newTime);
    };

    const handleSkip = (seconds) => {
        if (ytPlayerRef.current) {
            const current = (ytPlayerRef.current.getCurrentTime && ytPlayerRef.current.getCurrentTime()) || currentTime || 0;
            const newTime = Math.max(0, Math.min(duration, current + seconds));
            ytPlayerRef.current.seekTo(newTime, true);
            setCurrentTime(newTime);
            return;
        }
        if (!audioRef.current) return;
        const newTime = Math.max(0, Math.min(duration, audioRef.current.currentTime + seconds));
        audioRef.current.currentTime = newTime;
        setCurrentTime(newTime);
    };

    const handleSpeedChange = (rate) => {
        const snapped = Math.round(rate * 10) / 10;
        if (ytPlayerRef.current && ytPlayerRef.current.setPlaybackRate) {
            ytPlayerRef.current.setPlaybackRate(snapped);
        }
        if (audioRef.current) {
            audioRef.current.playbackRate = snapped;
        }
        setPlaybackRate(snapped);
    };

    // Seek directly to lyric line on click (only when line background / non-word is clicked)
    const handleLineClick = (line, idx) => {
        userClickedLineRef.current = true;
        const seekTime = line.start_ms / 1000;
        if (ytPlayerRef.current) {
            ytPlayerRef.current.seekTo(seekTime, true);
            setCurrentTime(seekTime);
            if (typeof idx === "number") {
                setActiveLineIndex(idx);
            }
            if (!isPlaying) {
                ytPlayerRef.current.playVideo();
                setIsPlaying(true);
            }
            return;
        }
        if (!audioRef.current) return;
        audioRef.current.currentTime = seekTime;
        setCurrentTime(seekTime);
        if (typeof idx === "number") {
            setActiveLineIndex(idx);
        }
        if (!isPlaying) {
            audioRef.current.play().then(() => setIsPlaying(true)).catch(console.error);
        }
    };

    // Toggle word saved state (add or remove)
    const handleWordClick = (e, wordClean, line) => {
        e.stopPropagation();
        if (!wordClean) return;
        const wordLower = wordClean.toLowerCase();
        const isSaved = savedWordsSet.has(wordLower);

        if (isSaved) {
            handleRemoveWord(wordClean);
        } else {
            // Lookup translation from song glossary
            let translation = "";
            if (song && song.word_glossary) {
                translation = song.word_glossary[wordClean] || song.word_glossary[wordLower] || "";
            }

            const optimisticWord = {
                id: `temp_${Date.now()}`,
                lang_id: langId,
                song_id: songId,
                song_title: song.title,
                artist: song.artist,
                word: wordClean,
                translation: translation,
                context_line: line.foreign || line.text || "",
                context_translation: line.english || line.translation || ""
            };

            setSavedWords((prev) => [...prev, optimisticWord]);
            showToast(`Added "${wordClean}" to Review Deck!`);

            authFetch(`${API_BASE_URL}/api/song-words`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    lang_id: langId,
                    song_id: songId,
                    song_title: song.title,
                    artist: song.artist,
                    word: wordClean,
                    translation: translation,
                    context_line: line.foreign || line.text || "",
                    context_translation: line.english || line.translation || ""
                })
            })
                .then((res) => res.json())
                .then((data) => {
                    if (data.success && data.word) {
                        setSavedWords((prev) =>
                            prev.map((w) => (w.id === optimisticWord.id ? data.word : w))
                        );
                    }
                })
                .catch((err) => {
                    console.error("Failed to save song word:", err);
                    setSavedWords((prev) => prev.filter((w) => w.id !== optimisticWord.id));
                    showToast(`Failed to save "${wordClean}"`);
                });
        }
    };

    const handleRemoveWord = (wordOrClean) => {
        const wordLower = (wordOrClean || "").toLowerCase();
        const existing = savedWords.find((w) => (w.word || "").toLowerCase() === wordLower);
        if (!existing) return;

        setSavedWords((prev) => prev.filter((w) => (w.word || "").toLowerCase() !== wordLower));
        showToast(`Removed "${existing.word}"`);

        authFetch(`${API_BASE_URL}/api/song-words?song_id=${songId}&word=${encodeURIComponent(existing.word)}`, {
            method: "DELETE"
        }).catch((err) => {
            console.error("Failed to remove song word:", err);
            setSavedWords((prev) => [...prev, existing]);
            showToast(`Failed to remove "${existing.word}"`);
        });
    };

    const handlePracticeWords = () => {
        if (savedWords.length === 0) return;
        window.location.href = `/review?song_id=${songId}`;
    };

    const showToast = (msg) => {
        setToastMessage(msg);
        setTimeout(() => {
            setToastMessage((cur) => (cur === msg ? null : cur));
        }, 3200);
    };

    const formatSeconds = (sec) => {
        if (!sec || isNaN(sec)) return "0:00";
        const m = Math.floor(sec / 60);
        const s = Math.floor(sec % 60);
        return `${m}:${s < 10 ? "0" : ""}${s}`;
    };

    if (loading) {
        return (
            <LoadingScreen
                title="Loading audio & lyrics..."
                onBack={() => navigateToDashboard("music")}
                backLabel="Dashboard"
            />
        );
    }

    if (error || !song) {
        return (
            <div className="song-player-root" style={{ padding: "40px", textAlign: "center" }}>
                <h2>Song Not Found</h2>
                <BackToDashboardButton
                    centered
                    onClick={() => navigateToDashboard("music")}
                />
            </div>
        );
    }

    const audioUrl = localAudioUrl || `${API_BASE_URL}/api/songs/${songId}/audio`;

    const renderSavedWordsContent = (isMobile = false) => (
        <>
            <div className="saved-sidebar__header">
                <div className="saved-sidebar__title-wrap">
                    <h3 className="saved-sidebar__title">Saved Words</h3>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span className="saved-sidebar__count-badge">{savedWords.length}</span>
                        {isMobile && (
                            <button
                                className="saved-drawer__close-btn"
                                onClick={() => setMobileMenuOpen(false)}
                                aria-label="Close"
                            >
                                ✕
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {savedWords.length === 0 ? (
                <div className="saved-sidebar__empty">
                    <p>No words saved yet</p>
                </div>
            ) : (
                <div className="saved-sidebar__list">
                    {savedWords.map((item, idx) => (
                        <div key={item.id || `${item.word}-${idx}`} className="saved-sidebar__item">
                            <div className="saved-sidebar__item-info">
                                <div className="saved-sidebar__word-row">
                                    <span className="saved-sidebar__word">{item.word}</span>
                                    <button
                                        className="saved-sidebar__speak-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            speakText(item.word, getLangTag(langId));
                                        }}
                                        title="Hear pronunciation"
                                        aria-label={`Pronounce ${item.word}`}
                                    >
                                        <VolumeIcon size={14} />
                                    </button>
                                </div>
                                {item.translation && (
                                    <span className="saved-sidebar__trans">{item.translation}</span>
                                )}
                            </div>
                            <button
                                className="saved-sidebar__remove-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveWord(item.word);
                                }}
                                title={`Remove "${item.word}"`}
                                aria-label={`Remove ${item.word}`}
                            >
                                ✕
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <div className="saved-sidebar__footer">
                <button
                    className="practice-words-btn"
                    disabled={savedWords.length === 0}
                    onClick={() => {
                        if (isMobile) setMobileMenuOpen(false);
                        handlePracticeWords();
                    }}
                >
                    <span>Practice These Words ({savedWords.length})</span>
                </button>
            </div>
        </>
    );

    return (
        <div className="song-player-root">
            {/* Native audio element (for preloaded course tracks) */}
            {!ytVideoId && (
                <audio
                    ref={audioRef}
                    src={audioUrl}
                    onTimeUpdate={handleTimeUpdate}
                    onLoadedMetadata={handleLoadedMetadata}
                    onEnded={() => setIsPlaying(false)}
                />
            )}

            {/* YouTube IFrame Player (rendered in fixed offscreen container for direct residential streaming) */}
            {ytVideoId && (
                <div
                    style={{
                        position: "fixed",
                        bottom: "-250px",
                        right: "-250px",
                        width: "200px",
                        height: "200px",
                        opacity: 0.01,
                        pointerEvents: "none",
                        zIndex: -1
                    }}
                >
                    <div id="yt-player-embed" />
                </div>
            )}

            {/* Top Fixed Header & Audio Controls */}
            <div className="song-player-top-bar">
                {/* Top Navigation Header */}
                <header className="song-player-header">
                    <div className="song-player-header__left">
                        <BackToDashboardButton onClick={() => navigateToDashboard("music")} />

                        {song.cover_image && (
                            <img
                                src={song.cover_image}
                                alt={song.title}
                                className="song-player-header__cover"
                            />
                        )}

                        <div className="song-player-header__title-block">
                            <h1 className="song-player-header__title">{song.title}</h1>
                            <p className="song-player-header__subtitle">
                                {song.artist} {song.album ? `• ${song.album}` : ""}
                            </p>
                        </div>
                    </div>

                    <div className="song-player-header__controls">
                        <button
                            className="toggle-switch-btn toggle-switch-btn--mobile-only"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            title="View saved words"
                        >
                            <span>Saved Words ({savedWords.length})</span>
                        </button>

                        <button
                            className={`toggle-switch-btn ${showTranslations ? "toggle-switch-btn--active" : ""}`}
                            onClick={() => setShowTranslations(!showTranslations)}
                            title="Toggle English lyric translations"
                        >
                            <TranslateIcon />
                            <span>{showTranslations ? "Hide Translations" : "Show Translations"}</span>
                        </button>

                        <button
                            className={`toggle-switch-btn ${autoScroll ? "toggle-switch-btn--active" : ""}`}
                            onClick={() => setAutoScroll(!autoScroll)}
                            title="Toggle automatic scrolling with active lyrics"
                        >
                            <span>{autoScroll ? "Auto-scroll ON" : "Auto-scroll OFF"}</span>
                        </button>
                    </div>
                </header>

                {/* Audio Control Bar */}
                <section className="audio-control-bar">
                    {/* Seeker Slider */}
                    <div className="audio-control-bar__seeker-row">
                        <span className="audio-control-bar__time">{formatSeconds(currentTime)}</span>
                        <input
                            type="range"
                            className="audio-control-bar__slider"
                            min="0"
                            max={duration || 100}
                            step="0.1"
                            value={currentTime}
                            onChange={(e) => handleSeek(parseFloat(e.target.value))}
                        />
                        <span className="audio-control-bar__time">{formatSeconds(duration)}</span>
                    </div>

                    {/* Playback Controls & Speed */}
                    <div className="audio-control-bar__actions-row">
                        <div className="audio-control-bar__main-btns">
                            <button
                                className="seek-step-btn"
                                onClick={() => handleSkip(-5)}
                                title="Rewind 5 seconds"
                            >
                                <Rewind5Icon />
                            </button>

                            <button className="play-pause-btn" onClick={togglePlay} title={isPlaying ? "Pause" : "Play"}>
                                {isPlaying ? <PauseIcon /> : <PlayIcon />}
                            </button>

                            <button
                                className="seek-step-btn"
                                onClick={() => handleSkip(5)}
                                title="Forward 5 seconds"
                            >
                                <Forward5Icon />
                            </button>
                        </div>

                        <div className="audio-control-bar__sub-controls">
                            <div className="audio-control-bar__speed-control">
                                <span className="audio-control-bar__speed-label">
                                    Speed: <strong>{playbackRate.toFixed(1)}x</strong>
                                </span>
                                <input
                                    type="range"
                                    className="audio-control-bar__speed-slider"
                                    min="0.5"
                                    max="1.5"
                                    step="0.1"
                                    value={playbackRate}
                                    onChange={(e) => handleSpeedChange(parseFloat(e.target.value))}
                                    title={`Playback speed: ${playbackRate.toFixed(1)}x`}
                                    aria-label="Playback speed"
                                />
                            </div>

                            <div className="audio-control-bar__sync-control" title="Fine-tune timing if vocals and lyrics are offset">
                                <span className="audio-control-bar__sync-label">
                                    Sync: <strong>{syncOffset > 0 ? `+${syncOffset.toFixed(1)}s` : `${syncOffset.toFixed(1)}s`}</strong>
                                </span>
                                <div className="audio-control-bar__sync-btns">
                                    <button
                                        className="sync-adjust-btn"
                                        onClick={() => setSyncOffset((prev) => Math.round((prev - 0.5) * 10) / 10)}
                                        title="Shift lyrics 0.5s earlier"
                                    >
                                        -0.5s
                                    </button>
                                    <button
                                        className="sync-adjust-btn"
                                        onClick={() => setSyncOffset((prev) => Math.round((prev + 0.5) * 10) / 10)}
                                        title="Shift lyrics 0.5s later"
                                    >
                                        +0.5s
                                    </button>
                                    {syncOffset !== 0 && (
                                        <button
                                            className="sync-adjust-btn sync-adjust-btn--reset"
                                            onClick={() => setSyncOffset(0)}
                                            title="Reset timing offset"
                                        >
                                            Reset
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            {/* Main Content Area: Synced Lyrics Viewport + Pinned Sidebar */}
            <div className="song-player-body">
                <main className="lyrics-viewport" ref={viewportRef}>
                    <div className="lyrics-container">
                        {song.lines && song.lines.length > 0 ? (
                            song.lines.map((line, idx) => {
                                const isActive = idx === activeLineIndex;
                                const tokens = tokenizeLyricLine(line.foreign || line.text);
                                const hasAnySavedWord = tokens.some(
                                    (t) => t.isWord && savedWordsSet.has(t.clean.toLowerCase())
                                );

                                return (
                                    <div
                                        key={idx}
                                        ref={(el) => (lineElementsRef.current[idx] = el)}
                                        className={`lyric-line-row ${isActive ? "lyric-line-row--active" : ""} ${hasAnySavedWord ? "lyric-line-row--bookmarked" : ""
                                            }`}
                                        onClick={() => handleLineClick(line, idx)}
                                        title="Click to jump audio to this line"
                                    >
                                        <div className="lyric-line-row__text-block">
                                            <div className="lyric-line-row__foreign">
                                                {tokens.map((token, tIdx) => {
                                                    if (!token.isWord) {
                                                        return (
                                                            <span key={tIdx} className="lyric-punct">
                                                                {token.raw}
                                                            </span>
                                                        );
                                                    }
                                                    const isSaved = savedWordsSet.has(token.clean.toLowerCase());
                                                    return (
                                                        <span
                                                            key={tIdx}
                                                            className={`lyric-word ${isSaved ? "lyric-word--saved" : ""}`}
                                                            onClick={(e) => handleWordClick(e, token.clean, line)}
                                                            title={
                                                                isSaved
                                                                    ? `Click to remove "${token.clean}" from review`
                                                                    : `Click to save "${token.clean}" for review`
                                                            }
                                                        >
                                                            {token.raw}
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                            {showTranslations && (line.english || line.translation) && (
                                                <div className="lyric-line-row__english">
                                                    {line.english || line.translation}
                                                </div>
                                            )}
                                        </div>

                                        <div className="lyric-line-row__actions">
                                            <span className="lyric-line-row__time-tag">
                                                {line.start_time || formatSeconds(line.start_ms / 1000)}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <p style={{ textAlign: "center", color: "#64748b" }}>No synchronized lines available.</p>
                        )}
                    </div>
                </main>

                {/* Pinned Desktop Sidebar */}
                <aside className="song-saved-sidebar">
                    {renderSavedWordsContent(false)}
                </aside>
            </div>

            {/* Mobile Drawer Overlay */}
            {mobileMenuOpen && (
                <div className="song-saved-drawer-overlay" onClick={() => setMobileMenuOpen(false)}>
                    <div className="song-saved-drawer-mobile" onClick={(e) => e.stopPropagation()}>
                        {renderSavedWordsContent(true)}
                    </div>
                </div>
            )}

            {/* Toast Notification */}
            {toastMessage && (
                <div className="toast-notice">
                    <CheckmarkIcon size={16} />
                    <span>{toastMessage}</span>
                </div>
            )}
        </div>
    );
}

export default SongPage;
